import { useCallback, useEffect, useRef } from 'react';

export type Response = {
	onAnimateStart: (el: HTMLElement) => void;
	onAnimateEnd: (el: HTMLElement) => void;
};

/**
 * Utility hook to allow animations to be synchronised for a collection of DOM elements that will be looped at a fixed
 * interval.
 */
export const useSynchronisedAnimation = (
	baseKeyframeEffect: KeyframeEffect | null,
	interval: number,
): Response => {
	const animationMap = useRef(new Map());
	const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

	const playAnimations = useCallback(() => {
		// Restart all animations in our map
		animationMap.current.forEach((animation) => {
			// eslint-disable-next-line no-param-reassign
			animation.currentTime = 0;
			animation.play();
		});
	}, []);

	const onAnimateStart = useCallback(
		(element: Element | null) => {
			// Silently exit if KeyframeEffect is not available. It is available in all supported browsers but we'll
			// check anyway so the app doesn't crash.

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			if (!baseKeyframeEffect || !window.KeyframeEffect) {
				return;
			}

			// Clone our keyframe effect and set it onto the new target
			const keyframe = new KeyframeEffect(baseKeyframeEffect);
			keyframe.target = element;
			// Create a new animation for our element
			animationMap.current.set(element, new Animation(keyframe));

			if (intervalRef.current === undefined) {
				intervalRef.current = setInterval(playAnimations, interval);
				// Ensure initial group of animations are started at the same time, e.g. when clicking restore defaults.
				queueMicrotask(playAnimations);
			}
		},
		[baseKeyframeEffect, interval, playAnimations],
	);

	const onAnimateEnd = useCallback((element: Element | null) => {
		if (!animationMap.current.has(element)) {
			return;
		}

		// Cancel the element animation and remove it from the map
		animationMap.current.get(element).cancel();
		animationMap.current.delete(element);

		// Clear our animation interval when there are no elements to be animated
		if (animationMap.current.size === 0) {
			clearInterval(intervalRef.current);
			intervalRef.current = undefined;
		}
	}, []);

	// End all pending animations on unmount
	useEffect(
		() => () => {
			animationMap.current.forEach((_, element) => onAnimateEnd(element));
		},
		[onAnimateEnd],
	);

	return {
		onAnimateStart,
		onAnimateEnd,
	};
};
